import {
    IonButton,
    IonCard,
    IonContent,
    IonFooter,
    IonPage,
    useIonRouter,
} from "@ionic/react";
import { useTranslation } from "react-i18next";
import React, { FC, useEffect } from "react";
import "../login/login.css";
import { FieldValues, FormProvider, useForm } from "react-hook-form";
import { updateProfile, formatProfile } from "../../services/profileService";
import { routes } from "../../route/constants";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { HeaderComponent, UserInfoComponent } from "../../components";
import {
    setLoggedInOrRegisterComplete,
    setShowOnBoardingFlow,
} from "../../redux/actions/appStatus";
import { mpCreateUserProfile } from "../../services/analytics/mixpanelService";
import { UserProfile } from "../../models/userProfile";
import { ConfigPhone } from "refsix-js-models";
import { getMisconductByLocale } from "refsix-core";
import { getLocale } from "../../utils/localeUtils";
import { updateSettings } from "../../services/settingsService";
import moment from "moment";

const RegisterStepTwo: FC = () => {
    const { t } = useTranslation();
    const profile = useSelector((root: RootState) => root.currentUser?.profile);
    const session = useSelector((root: RootState) => root.auth.session);
    const settings = useSelector((root: RootState) => root.settings);
    const storedEmail = useSelector((root: RootState) => root.user?.usernameOrEmail);
    // Use session user_id as fallback when profile hasn't loaded yet
    const username = profile?.username || session?.user_id;
    const route = useIonRouter();
    const dispatch = useDispatch();
    const formMethods = useForm<FieldValues>({
        mode: "all",
        defaultValues: {
            firstName: profile?.firstName || "",
            lastName: profile?.lastName || "",
            dob: profile?.dob ? moment(profile.dob).format("YYYY-MM-DD") : "",
        },
    });
    const profileExists = profile !== undefined;
    useEffect(() => {
        if (profileExists) {
            const resetData = {
                firstName: profile.firstName || "",
                lastName: profile.lastName || "",
                dob: profile.dob
                    ? moment(profile.dob).format("YYYY-MM-DD")
                    : "",
            };
            formMethods.reset(resetData);
        }
    }, [profileExists]);
    const {
        handleSubmit,
        formState: { isValid },
    } = formMethods;

    // just for use by the shouldGoStraightToFreeTrial variant

    const submitUpdate = async (profileFields: FieldValues) => {
        if (username) {
            // Format the DOB if provided
            const formattedFields = { ...profileFields };
            if (formattedFields.dob) {
                formattedFields.dob = moment(formattedFields.dob).toISOString();
            }

            const updatedProfile: UserProfile = {
                email: profile?.email || storedEmail || "",
                firstName: formattedFields.firstName || profile?.firstName || "",
                lastName: formattedFields.lastName || profile?.lastName || "",
                country: profile?.country || "",
                localFA: profile?.localFA || "",
                nationalFA: profile?.nationalFA || "",
                refereeLevel: profile?.refereeLevel || "",
                ...formattedFields,
                username: username,
                biometrics: {},
            };
            try {
                const formattedProfile = formatProfile(updatedProfile);
                await updateProfile(username, formattedProfile);

                // Create user profile in
                mpCreateUserProfile(updatedProfile, false);

                dispatch(setLoggedInOrRegisterComplete(true));
                dispatch(setShowOnBoardingFlow(true));

                // update the misconduct code on the settings
                console.log(
                    "detected default misconduct code of ",
                    getMisconductByLocale(
                        getLocale(),
                        updatedProfile.nationalFA
                    )
                );

                const updatedConfig: ConfigPhone = {
                    ...settings.settings,
                    misconductCode: getMisconductByLocale(
                        getLocale(),
                        updatedProfile.nationalFA
                    ),
                };

                await updateSettings({ ...settings, settings: updatedConfig });

                // do onboarding flow
                route.push(routes.registerStepThree);
            } catch (e) {
                console.error(e);
            }
        }
    };

    return (
        <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(submitUpdate)}>
                <IonPage>
                    <HeaderComponent
                        title={t("myaccount.profileHeader")}
                        showProgressBar={true}
                        progressValue={0.4}
                    />

                    <IonContent>
                        <h2 className="onboarding-section-header">
                            {t("register.registerOrLogin.letUsKnow")}
                        </h2>

                        <IonCard className="match-card-container">
                            <UserInfoComponent />
                        </IonCard>
                    </IonContent>
                    <IonFooter>
                        <IonButton
                            expand="block"
                            onClick={handleSubmit(submitUpdate)}
                            className="login-button"
                            data-testid="stepTwoButton"
                            disabled={!isValid}
                        >
                            {t("general.next")}
                        </IonButton>
                    </IonFooter>
                </IonPage>
            </form>
        </FormProvider>
    );
};

export default RegisterStepTwo;
